import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useMyProvider } from '../../hooks/useMyProvider';
import SearchBar from '../SearchBar';

// Import local images
import cloutLogo from '../../assets/logos/clout-logo.png';

// Icons
const NotificationIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const { provider } = useMyProvider();

  const analyticsNavLinks = [
    { path: '/dashboard/analytics', label: 'Overview', icon: '📊' },
    { path: '/dashboard/analytics/sentiment', label: 'Sentiment', icon: '😊' },
    { path: '/dashboard/analytics/topics', label: 'Topics', icon: '💬' },
    { path: '/dashboard/analytics/alerts', label: 'Alerts', icon: '🔔' },
  ];

  return (
    <div className="min-h-screen bg-[#f8f6f2]">
      {/* Header - Same as Dashboard/Endorsements/WhatsHot */}
      <header className="bg-[#f8f6f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img src={cloutLogo} alt="CLOUT Aesthetics" className="h-10 sm:h-14" />
            </div>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-lg mx-8">
              <SearchBar placeholder="Search for providers" isAuthenticated={true} />
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button className="p-2 hover:bg-white/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
                <NotificationIcon />
              </button>
              <button
                onClick={() => provider && navigate(`/provider/${provider.id}`)}
                className="p-2 hover:bg-white/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <UserIcon />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Navigation - Same structure as Dashboard/Endorsements/WhatsHot */}
      <nav className="bg-white border-t border-b border-[#eaeaea]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center overflow-x-auto no-scrollbar">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              HOME
            </button>
            <button
              onClick={() => navigate('/endorsements')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              ENDORSEMENTS
            </button>
            <button
              onClick={() => navigate('/whats-hot')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              WHAT'S HOT!
            </button>
            <button className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-white uppercase bg-[#1f1f1f] whitespace-nowrap min-h-[44px]">
              ANALYTICS
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Search Bar */}
      <div className="md:hidden bg-white border-b border-[#eaeaea] px-4 py-3">
        <SearchBar placeholder="Search for providers" isAuthenticated={true} />
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Analytics Sub-Navigation - Inside content area */}
        <div className="flex space-x-1 overflow-x-auto no-scrollbar mb-6 sm:mb-8">
          {analyticsNavLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === '/dashboard/analytics'}
              className={({ isActive }) =>
                `px-4 md:px-6 py-2 text-xs md:text-sm font-['Open_Sans'] font-medium uppercase whitespace-nowrap rounded transition-colors min-h-[40px] flex items-center ${
                  isActive ? 'bg-[#c6a664] text-white' : 'text-[#1f1f1f] hover:bg-white border border-[#eaeaea]'
                }`
              }
            >
              <span className="mr-2">{link.icon}</span>
              {link.label}
            </NavLink>
          ))}
        </div>

        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
